// The `issue` wire shape — one projection, consumed by `GET /v1/issues/:id`,
// `GET /v1/repos/:o/:r/issues` and the MCP `issue` verb's `list`/`get`.
//
// Those doors carried two hand-written copies of this body (`projectIssue` in
// `apps/api/src/routes/issues.ts`, `maskIssue` in
// `packages/mcp/src/army/issue.ts`) that differed only in a comment and a
// return type — the MCP copy was typed `Record<string, unknown>`, so nothing
// would have failed had they drifted. They are one function now.

import { z } from 'zod';
import { defineResource } from './resource';

/**
 * The camelCase shape the projection reads — structural on purpose, so the
 * resource states its own minimum rather than pulling in an entity's full
 * schema. `Issue` (`entities/issue.ts`) satisfies it, and `issue.test.ts`
 * proves that by serializing a parsed one.
 */
export type IssueEntity = {
  id: string;
  repoId: string;
  githubNumber: number;
  githubId: number;
  title: string;
  state: string;
  qualification: string;
  handoffStatus: string | null;
  lastSessionId: string | null;
  lastAgentActionAt: Date | null;
};

/** The published body. Field names and nullability are the existing contract. */
export const IssueWireSchema = z.object({
  id: z.string(),
  repo_id: z.string(),
  github_number: z.number(),
  github_id: z.number(),
  /** Cached GitHub title (#980); '' on pre-cache rows — renderers fall back to #<n>. */
  title: z.string(),
  state: z.string(),
  qualification: z.string(),
  handoff_status: z.string().nullable(),
  last_session_id: z.string().nullable(),
  last_agent_action_at: z.string().nullable(),
});

/** What a client should type against for an issue body. */
export type IssueWire = z.infer<typeof IssueWireSchema>;

export const IssueResource = defineResource<IssueEntity, typeof IssueWireSchema>({
  schema: IssueWireSchema,
  serialize: (i) => ({
    id: i.id,
    repo_id: i.repoId,
    github_number: i.githubNumber,
    github_id: i.githubId,
    title: i.title,
    state: i.state,
    qualification: i.qualification,
    handoff_status: i.handoffStatus,
    last_session_id: i.lastSessionId,
    last_agent_action_at: i.lastAgentActionAt ? i.lastAgentActionAt.toISOString() : null,
  }),
});
